import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { UploadInputMode } from "./UploadInputModeTabs";

interface UploadSubmitButtonProps {
  inputMode: UploadInputMode;
  isUploading: boolean;
  selectedFileCount: number;
  hasTextData: boolean;
  hasUrlData: boolean;
  onSubmit: () => void;
  /**
   * Extra disable signal bubbled up by the parent page — currently used by
   * Upload.tsx to gate the submit button until the AuditContextForm's Q1
   * (communicationPurpose) is answered (pirx-c4dlb). When true, the button
   * is disabled regardless of file/text/url presence or uploading state.
   */
  disabled?: boolean;
}

export default function UploadSubmitButton({
  inputMode,
  isUploading,
  selectedFileCount,
  hasTextData,
  hasUrlData,
  onSubmit,
  disabled: disabledByParent = false,
}: UploadSubmitButtonProps) {
  const { t } = useTranslation(["upload"]);
  const disabled = (inputMode === "file" && selectedFileCount === 0) ||
    (inputMode === "text" && !hasTextData) ||
    (inputMode === "url" && !hasUrlData) ||
    isUploading ||
    // pirx-c4dlb: parent page can force-disable (e.g. until Q1 answered).
    disabledByParent;

  return (
    <Button
      variant="cta"
      size="xl"
      onClick={onSubmit}
      disabled={disabled}
      data-test="upload-submit-btn"
      className="mt-6 w-full gap-3"
    >
      {isUploading
        ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent">
            </div>
            <span>
              {inputMode === "text"
                ? t("upload:textInput.submitting")
                : inputMode === "url"
                ? t("upload:urlInput.submitting")
                : t("upload:uploading")}
            </span>
          </>
        )
        : inputMode === "file"
        ? (
          <>
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <span>
              {selectedFileCount > 0
                ? t("upload:uploadButton", { count: selectedFileCount })
                : t("upload:uploadFiles")}
            </span>
          </>
        )
        : (
          <>
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>
              {inputMode === "url"
                ? t("upload:urlInput.submit")
                : t("upload:textInput.submit")}
            </span>
          </>
        )}
    </Button>
  );
}
